import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  GraduationCap,
  PlayCircle,
  Lightbulb,
  FilmStrip,
  Plus,
  Trash,
  CircleNotch,
  PaperPlaneTilt,
  PencilSimple,
  X,
  Tractor,
  Plant,
  Drone,
  Cow,
} from "@phosphor-icons/react";
import {
  fetchAcademyCategories,
  addAcademyVideo,
  removeAcademyVideo,
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Admin-managed Academy: YouTube-Shorts per Backend-API verwaltet.
 *
 * Anfragen GET sind öffentlich lesbar, aber POST/DELETE verlangen is_admin.
 * Nicht-Admins sehen nur die kuratierte Ansicht ohne Bearbeiten-Button.
 */
const ICON_MAP = { Plant, Cow, Drone, Tractor };

export default function Academy() {
  const { user } = useAuth();
  const [categories, setCategories] = useState(null);
  const [cat, setCat] = useState("acker");
  const [active, setActive] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const reload = async () => {
    try {
      const data = await fetchAcademyCategories();
      setCategories(data.categories || []);
    } catch {
      toast.error("Academy konnte nicht geladen werden.");
    }
  };

  useEffect(() => { reload(); }, []);

  const current = useMemo(
    () => (categories || []).find((c) => c.id === cat) || null,
    [categories, cat]
  );

  const isAdmin = user?.is_admin;

  return (
    <div className="space-y-6" data-testid="academy-root">
      <Helmet><title>Academy · Bodenblick</title></Helmet>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eid-label flex items-center gap-2">
            <GraduationCap size={12} /> Lehrvideos · YouTube-Shorts
          </div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">
            Bodenblick Academy
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Kuratierte Kurzvideos für Ackerbau, Grünland, Smart Farming und
            Tierhaltung. Ideal, um komplexe Themen in 30–90 Sekunden zu
            verstehen.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setEditMode((v) => !v)}
            data-testid="academy-edit-toggle"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm transition-all ${
              editMode
                ? "bg-[#FF5500] text-white border-[#FF5500]/40"
                : "bg-[#121212] border-white/10 hover:bg-white/5"
            }`}
          >
            {editMode ? <X size={14} /> : <PencilSimple size={14} />}
            {editMode ? "Bearbeiten beenden" : "Videos verwalten"}
          </button>
        )}
      </header>

      {!categories && (
        <div className="eid-card p-8 flex items-center justify-center gap-2 text-zinc-400" data-testid="academy-loading">
          <CircleNotch size={18} className="animate-spin" /> Lade Videos…
        </div>
      )}

      {categories && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="academy-tabs">
            {categories.map((c) => {
              const Icon = ICON_MAP[c.icon] || GraduationCap;
              const isActive = c.id === cat;
              return (
                <button
                  key={c.id}
                  onClick={() => { setCat(c.id); setActive(null); }}
                  data-testid={`academy-tab-${c.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-all ${
                    isActive ? "shadow-sm" : "border-white/10 hover:bg-white/5"
                  }`}
                  style={
                    isActive
                      ? { background: c.color, color: "#FFFFFF", borderColor: c.color }
                      : { background: "#121212" }
                  }
                >
                  <Icon size={16} />
                  {c.label}
                  <span className={`text-[10px] font-mono ml-1 px-1.5 py-0.5 rounded-full ${
                    isActive ? "bg-white/25" : "bg-white/5 text-zinc-400"
                  }`}>
                    {(c.videos || []).length}
                  </span>
                </button>
              );
            })}
          </div>

          {isAdmin && editMode && current && (
            <AddVideoForm key={current.id} category={current} onAdded={reload} />
          )}

          <section
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="academy-grid"
          >
            {(current?.videos || []).length === 0 && (
              <div className="sm:col-span-2 lg:col-span-3 eid-card p-6 text-sm text-zinc-400">
                Noch keine Videos in dieser Kategorie.
                {isAdmin && ' Klicke oben auf „Videos verwalten\u201C, um welche hinzuzufügen.'}
              </div>
            )}
            {(current?.videos || []).map((v) => (
              <ShortCard
                key={v.id}
                video={v}
                accent={current.color}
                active={active === v.id}
                onPlay={() => setActive(v.id)}
                canEdit={isAdmin && editMode}
                onDelete={async () => {
                  if (!window.confirm(`Video „${v.title}" wirklich entfernen?`)) return;
                  try {
                    await removeAcademyVideo(current.id, v.id);
                    toast.success("Video entfernt");
                    reload();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
                  }
                }}
              />
            ))}
          </section>
        </>
      )}

      <div
        className="eid-card p-5 border-l-4 bg-[#0A0A0A]"
        style={{ borderLeftColor: "#00FA9A" }}
        data-testid="academy-cta"
      >
        <div className="flex items-start gap-3">
          <span className="shrink-0 p-2 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]">
            <Lightbulb size={18} weight="fill" />
          </span>
          <div>
            <h3 className="text-base font-medium">Eigene Shorts hinzufügen</h3>
            <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
              Lade deine Lehrvideos als YouTube-Shorts hoch (≤ 60 Sek, vertikal 9:16)
              oder nutze fremde öffentliche Videos. Als Admin: oben rechts auf
              <strong> „Videos verwalten"</strong> klicken und URL + Titel eintragen —
              fertig. Kein Deploy nötig.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-wider">
              <span className="px-2 py-1 rounded-full bg-[#121212] border border-white/10 text-zinc-400">YouTube Shorts</span>
              <span className="px-2 py-1 rounded-full bg-[#121212] border border-white/10 text-zinc-400">Instagram Reels (Link in Bio)</span>
              <span className="px-2 py-1 rounded-full bg-[#121212] border border-white/10 text-zinc-400">TikTok (Cross-Post)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddVideoForm({ category, onAdded }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [creator, setCreator] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim() || !title.trim()) {
      toast.error("URL und Titel sind Pflicht");
      return;
    }
    setLoading(true);
    try {
      await addAcademyVideo(category.id, { url: url.trim(), title: title.trim(), creator: creator.trim() });
      toast.success(`Video hinzugefügt in ${category.label}`);
      setUrl(""); setTitle(""); setCreator("");
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hinzufügen fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="eid-card p-4 space-y-3 border-l-4"
      style={{ borderLeftColor: category.color }}
      data-testid="academy-add-form"
    >
      <div className="flex items-center gap-2">
        <span className="p-1.5 rounded-sm" style={{ background: `${category.color}22`, color: category.color }}>
          <Plus size={14} weight="bold" />
        </span>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
            Neues Video hinzufügen · {category.label}
          </div>
          <div className="text-sm text-white">Einfach YouTube-URL einfügen, Titel vergeben, fertig.</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[2fr,1.5fr,1fr,auto] gap-2">
        <input
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/shorts/…"
          data-testid="academy-add-url"
          className="px-3 py-2 text-sm bg-[#121212] border border-white/10 rounded-sm focus:outline-none focus:border-[#00FA9A]/40 font-mono"
        />
        <input
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Video-Titel"
          data-testid="academy-add-title"
          className="px-3 py-2 text-sm bg-[#121212] border border-white/10 rounded-sm focus:outline-none focus:border-[#00FA9A]/40"
        />
        <input
          value={creator}
          onChange={(e) => setCreator(e.target.value)}
          placeholder="Creator (optional)"
          data-testid="academy-add-creator"
          className="px-3 py-2 text-sm bg-[#121212] border border-white/10 rounded-sm focus:outline-none focus:border-[#00FA9A]/40"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="academy-add-submit"
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-[#00FA9A] hover:bg-[#33FBAE] text-white rounded-sm text-sm font-medium disabled:opacity-50"
        >
          {loading ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />}
          Hinzufügen
        </button>
      </div>
      <p className="text-[11px] text-zinc-500 leading-snug">
        Unterstützt: <code className="font-mono">youtube.com/shorts/…</code>,
        <code className="font-mono"> youtu.be/…</code>,
        <code className="font-mono"> youtube.com/watch?v=…</code> oder direkt die 11-Zeichen-ID.
      </p>
    </form>
  );
}

function ShortCard({ video, accent, active, onPlay, canEdit, onDelete }) {
  const src = `https://www.youtube-nocookie.com/embed/${video.id}?rel=0&modestbranding=1`;
  return (
    <article
      className="eid-card overflow-hidden flex flex-col relative"
      data-testid={`academy-card-${video.id}`}
      style={{ borderTop: `3px solid ${accent}` }}
    >
      {canEdit && (
        <button
          onClick={onDelete}
          data-testid={`academy-delete-${video.id}`}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-[#FF5500] hover:bg-[#8A3518] text-white shadow-md"
          aria-label="Video löschen"
        >
          <Trash size={12} weight="bold" />
        </button>
      )}
      <div className="aspect-[9/16] bg-white/10 relative overflow-hidden" style={{ maxHeight: 520 }}>
        {active ? (
          <iframe
            src={`${src}&autoplay=1`}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid={`academy-iframe-${video.id}`}
          />
        ) : (
          <button
            onClick={onPlay}
            className="absolute inset-0 w-full h-full bg-cover bg-center flex items-center justify-center group"
            style={{ backgroundImage: `url(https://img.youtube.com/vi/${video.id}/hqdefault.jpg)` }}
            data-testid={`academy-play-${video.id}`}
            aria-label={`Abspielen: ${video.title}`}
          >
            <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 transition-opacity group-hover:opacity-90" />
            <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-white bg-black/40 backdrop-blur px-1.5 py-0.5 rounded-sm">
              DSGVO · Klick lädt YouTube
            </span>
            <span className="relative z-10 rounded-full bg-black/70 backdrop-blur-xl text-white shadow-xl p-3 transition-transform group-hover:scale-110">
              <PlayCircle size={44} weight="fill" />
            </span>
          </button>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          <FilmStrip size={10} /> Short{video.creator ? ` · ${video.creator}` : ""}
        </div>
        <h3 className="text-sm font-medium mt-0.5 leading-snug">{video.title}</h3>
      </div>
    </article>
  );
}
